import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  commonUtils,
  Headline,
  IconButton,
  Label,
  StaticNotification,
} from '@sato/components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { t } from '@stores/localization';

import { allPaints, whitePaints } from '../RenovationPaintingKitForm.utils';

type ColorSelectionsSummaryProps = {
  className?: string;
};

export const ColorSelectionsSummary = observer(
  ({ className }: ColorSelectionsSummaryProps) => {
    const formContext = useFormContext<{
      colorShades: string[];
      whiteShades: string[];
    }>();

    const [colorShades = [], whiteShades = []] = formContext.watch([
      'colorShades',
      'whiteShades',
    ]);

    const selectedColors = [...colorShades, ...whiteShades]
      .map((key) => allPaints.find((paint) => paint.value === key))
      .filter(commonUtils.nonNullable);

    return (
      <div className={classNames('flex flex-col', className)}>
        <Headline level={2} className="mb-3">
          {t('orderSummary')}
        </Headline>

        {!selectedColors.length && (
          <StaticNotification
            variant="darkened"
            body={t('renovation.paintColorsSummaryNoColorsSelected')}
          />
        )}

        {selectedColors.map(({ label, hexCode, value }) => (
          <div
            key={value}
            className="flex flex-col gap-2 md:mb-2 md:flex-row md:items-center"
          >
            <div className="flex items-center gap-2 md:mb-4">
              <div
                className="h-4 w-4 rounded-full"
                style={{ backgroundColor: hexCode }}
              />

              <Label component="p">{label}</Label>

              <IconButton
                icon="remove24"
                variant="ghost"
                iconClassName="text-6"
                onClick={() => {
                  const isWhiteShade = whitePaints.some(
                    (shade) => shade.value === value
                  );

                  if (isWhiteShade) {
                    formContext.setValue(
                      'whiteShades',
                      whiteShades.filter((shade) => shade !== value)
                    );
                  } else {
                    formContext.setValue(
                      'colorShades',
                      colorShades.filter((shade) => shade !== value)
                    );
                  }
                }}
              />
            </div>

            <HookFormTextArea
              name={`useCasesForPaints.${value}`}
              rows={2}
              wrapperClassName="flex-1"
              placeholder={t('renovation.paintUseCaseInputPlaceholder')}
              maxLength={300}
            />
          </div>
        ))}
      </div>
    );
  }
);
